import { useState, useEffect } from 'react';
import { fetchDuplicateEmojis, DuplicateEmojiInfo } from '@/lib/api';

export type DuplicateFilterType = 'doubles' | 'triples' | 'double-doubles';

interface DuplicateEmojiFilterProps {
  collectionAddress: string;
  onFilterChange: (filter: { type: DuplicateFilterType; emojis: string[] } | null) => void;
  activeFilter: { type: DuplicateFilterType; emojis: string[] } | null;
}

export default function DuplicateEmojiFilter({
  collectionAddress,
  onFilterChange,
  activeFilter,
}: DuplicateEmojiFilterProps) {
  const [availableEmojis, setAvailableEmojis] = useState<DuplicateEmojiInfo[]>([]);
  const [doubleDoublesCount, setDoubleDoublesCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [doublesExpanded, setDoublesExpanded] = useState(false);
  const [doubleDoublesExpanded, setDoubleDoublesExpanded] = useState(false);
  const [triplesExpanded, setTriplesExpanded] = useState(false);

  useEffect(() => {
    async function loadAvailableEmojis() {
      setIsLoading(true);
      try {
        // Fetch with minCount=2 to get all emojis with doubles or triples
        const response = await fetchDuplicateEmojis(collectionAddress, {
          minCount: 2,
          limit: 1, // We only need the availableEmojis summary
        });
        setAvailableEmojis(response.availableEmojis);
        setDoubleDoublesCount(response.doubleDoublesCount || 0);
      } catch (error) {
        console.error('Failed to load duplicate emoji data:', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadAvailableEmojis();
  }, [collectionAddress]);

  // Filter and sort emojis - selected ones float to top
  const doublesEmojis = availableEmojis
    .filter(e => e.doublesCount > 0 || e.triplesCount > 0)
    .sort((a, b) => {
      const aSelected = activeFilter?.type === 'doubles' && activeFilter.emojis.includes(a.emoji);
      const bSelected = activeFilter?.type === 'doubles' && activeFilter.emojis.includes(b.emoji);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

  const doubleDoublesEmojis = availableEmojis
    .filter(e => e.doubleDoublesCount > 0)
    .sort((a, b) => {
      const aSelected = activeFilter?.type === 'double-doubles' && activeFilter.emojis.includes(a.emoji);
      const bSelected = activeFilter?.type === 'double-doubles' && activeFilter.emojis.includes(b.emoji);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

  const triplesEmojis = availableEmojis
    .filter(e => e.triplesCount > 0)
    .sort((a, b) => {
      const aSelected = activeFilter?.type === 'triples' && activeFilter.emojis.includes(a.emoji);
      const bSelected = activeFilter?.type === 'triples' && activeFilter.emojis.includes(b.emoji);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

  const handleEmojiClick = (type: 'doubles' | 'triples', emoji: string) => {
    const currentEmojis = activeFilter?.type === type ? activeFilter.emojis : [];

    if (currentEmojis.includes(emoji)) {
      // Remove emoji from selection
      const newEmojis = currentEmojis.filter(e => e !== emoji);
      if (newEmojis.length === 0) {
        onFilterChange(null);
      } else {
        onFilterChange({ type, emojis: newEmojis });
      }
    } else {
      // Add emoji to selection (switching type clears previous selection)
      if (activeFilter?.type && activeFilter.type !== type) {
        // Switching from doubles to triples or vice versa - start fresh
        onFilterChange({ type, emojis: [emoji] });
      } else {
        onFilterChange({ type, emojis: [...currentEmojis, emoji] });
      }
    }
  };

  const handleDoubleDoublesEmojiClick = (emoji: string) => {
    const currentEmojis = activeFilter?.type === 'double-doubles' ? activeFilter.emojis : [];

    if (currentEmojis.includes(emoji)) {
      // Remove emoji from selection
      const newEmojis = currentEmojis.filter(e => e !== emoji);
      if (newEmojis.length === 0) {
        // Clear filter entirely when last emoji deselected
        onFilterChange(null);
      } else {
        onFilterChange({ type: 'double-doubles', emojis: newEmojis });
      }
    } else {
      // Add emoji to selection
      if (activeFilter?.type && activeFilter.type !== 'double-doubles') {
        // Switching from another type - start fresh
        onFilterChange({ type: 'double-doubles', emojis: [emoji] });
      } else {
        onFilterChange({ type: 'double-doubles', emojis: [...currentEmojis, emoji] });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
          <div className="flex gap-2">
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (doublesEmojis.length === 0) {
    return null; // No duplicates found, don't show the filter
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-4">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Duplicate Emojis</h3>

      {/* Doubles Section */}
      {doublesEmojis.length > 0 && (
        <div className="mb-3">
          <button
            onClick={() => setDoublesExpanded(!doublesExpanded)}
            className="w-full flex items-center justify-between py-2 text-left"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Doubles (2x same emoji)
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                {doublesEmojis.length} emojis
              </span>
            </span>
            <div className="flex items-center gap-2">
              {/* Show selected emojis when collapsed */}
              {!doublesExpanded && activeFilter?.type === 'doubles' && activeFilter.emojis.length > 0 && (
                <div className="flex gap-1">
                  {activeFilter.emojis.map(emoji => (
                    <span key={emoji} className="w-8 h-8 text-xl flex items-center justify-center bg-blue-500 text-white rounded-lg">
                      {emoji}
                    </span>
                  ))}
                </div>
              )}
              <span className="text-gray-400 dark:text-gray-500">
                {doublesExpanded ? '−' : '+'}
              </span>
            </div>
          </button>

          {doublesExpanded && (
            <div className="flex flex-wrap gap-1 mt-2">
              {doublesEmojis.map((item) => {
                const isActive = activeFilter?.type === 'doubles' && activeFilter?.emojis.includes(item.emoji);
                const count = item.doublesCount + item.triplesCount;
                return (
                  <button
                    key={`double-${item.emoji}`}
                    onClick={() => handleEmojiClick('doubles', item.emoji)}
                    title={`${item.emoji} appears 2+ times in ${count} Skeles`}
                    className={`
                      w-10 h-10 text-2xl flex items-center justify-center rounded-lg
                      transition-all duration-150 hover:scale-105
                      ${isActive
                        ? 'bg-blue-500 text-white ring-2 ring-blue-600 ring-offset-1'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {item.emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Double Doubles Section - Skeles with 2+ different duplicate emoji combos */}
      {doubleDoublesCount > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mb-3">
          <button
            onClick={() => setDoubleDoublesExpanded(!doubleDoublesExpanded)}
            className="w-full flex items-center justify-between py-2 text-left"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Double Doubles (2+ combos)
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                {doubleDoublesCount} Skeles
              </span>
            </span>
            <div className="flex items-center gap-2">
              {/* Show selected emojis when collapsed */}
              {!doubleDoublesExpanded && activeFilter?.type === 'double-doubles' && activeFilter.emojis.length > 0 && (
                <div className="flex gap-1">
                  {activeFilter.emojis.map(emoji => (
                    <span key={emoji} className="w-8 h-8 text-xl flex items-center justify-center bg-amber-500 text-white rounded-lg">
                      {emoji}
                    </span>
                  ))}
                </div>
              )}
              <span className="text-gray-400 dark:text-gray-500">
                {doubleDoublesExpanded ? '−' : '+'}
              </span>
            </div>
          </button>

          {doubleDoublesExpanded && (
            <div className="flex flex-wrap gap-1 mt-2">
              {doubleDoublesEmojis.map((item) => {
                const isActive = activeFilter?.type === 'double-doubles' && activeFilter?.emojis.includes(item.emoji);
                const count = item.doubleDoublesCount;
                return (
                  <button
                    key={`doubledouble-${item.emoji}`}
                    onClick={() => handleDoubleDoublesEmojiClick(item.emoji)}
                    title={`Filter double doubles containing ${item.emoji} (${count} Skeles)`}
                    className={`
                      w-10 h-10 text-2xl flex items-center justify-center rounded-lg
                      transition-all duration-150 hover:scale-105
                      ${isActive
                        ? 'bg-amber-500 text-white ring-2 ring-amber-600 ring-offset-1'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {item.emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Triples Section */}
      {triplesEmojis.length > 0 && (
        <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
          <button
            onClick={() => setTriplesExpanded(!triplesExpanded)}
            className="w-full flex items-center justify-between py-2 text-left"
          >
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Triples (3x same emoji)
              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                {triplesEmojis.length} emojis
              </span>
            </span>
            <div className="flex items-center gap-2">
              {/* Show selected emojis when collapsed */}
              {!triplesExpanded && activeFilter?.type === 'triples' && activeFilter.emojis.length > 0 && (
                <div className="flex gap-1">
                  {activeFilter.emojis.map(emoji => (
                    <span key={emoji} className="w-8 h-8 text-xl flex items-center justify-center bg-purple-500 text-white rounded-lg">
                      {emoji}
                    </span>
                  ))}
                </div>
              )}
              <span className="text-gray-400 dark:text-gray-500">
                {triplesExpanded ? '−' : '+'}
              </span>
            </div>
          </button>

          {triplesExpanded && (
            <div className="flex flex-wrap gap-1 mt-2">
              {triplesEmojis.map((item) => {
                const isActive = activeFilter?.type === 'triples' && activeFilter?.emojis.includes(item.emoji);
                return (
                  <button
                    key={`triple-${item.emoji}`}
                    onClick={() => handleEmojiClick('triples', item.emoji)}
                    title={`${item.emoji} appears 3+ times in ${item.triplesCount} Skeles`}
                    className={`
                      w-10 h-10 text-2xl flex items-center justify-center rounded-lg
                      transition-all duration-150 hover:scale-105
                      ${isActive
                        ? 'bg-purple-500 text-white ring-2 ring-purple-600 ring-offset-1'
                        : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }
                    `}
                  >
                    {item.emoji}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Active filter indicator */}
      {activeFilter && activeFilter.emojis.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {activeFilter.type === 'double-doubles'
                ? `Showing Double Doubles with ${activeFilter.emojis.join(' ')}`
                : `Showing ${activeFilter.type === 'doubles' ? '2+' : '3+'} × ${activeFilter.emojis.join(' ')}`
              }
            </span>
            <button
              onClick={() => onFilterChange(null)}
              className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
